"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const react_1 = require("react");
const core_1 = require("@emotion/core");
const common_utils_1 = require("@zapier/common-utils");
const theme_1 = require("@zapier/theme");
const design_system_1 = require("@zapier/design-system");
const HELP_LINK_TEXT = 'Troubleshoot error';
const HELP_LINK_LABEL = 'Open a page with more information about this error.';
const TITLE_BUTTON_LABEL = 'Click to open this step of your Zap.';
const SHOW_DETAILS_TEXT = 'Show details';
const HIDE_DETAILS_TEXT = 'Hide details';
const ICON_SIZE = 25;
const TEXT_INSET = 40;
const containerCss = core_1.css `
  list-style-type: none;
  border-radius: 10px;
  border: 1px solid ${theme_1.Colors.red700};
  overflow: hidden;
  position: relative;

  & + & {
    margin-top: ${theme_1.Spacing.small}px;
  }
`;
const titleCss = core_1.css `
  background-color: ${theme_1.Colors.red100};
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  margin: 0;
  border: none;
  box-sizing: border-box;
  width: 100%;
`;
const titleButtonCss = core_1.css `
  ${titleCss}
  transition: background-color 0.2s;
  cursor: pointer;

  &:focus {
    background-color: ${theme_1.Colors.red200};
  }
`;
const titleTextCss = core_1.css `
  padding: 9px 0;
  flex: 1 0;
  text-align: left;
  align-self: center;

  &:last-child {
    padding-right: 12px;
  }
`;
const iconCss = core_1.css `
  display: block;
  height: ${TEXT_INSET}px;
  width: ${TEXT_INSET}px;
  display: flex;
  align-items: center;
  justify-content: center;
`;
const bodySmallCss = core_1.css `
  padding: ${theme_1.Spacing.medium}px ${TEXT_INSET}px;
  border-top: 1px solid ${theme_1.Colors.red200};
`;
const bodyMediumCss = core_1.css `
  ${bodySmallCss}
  display: flex;
  box-sizing: border-box;
  width: 100%;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`;
const bodyCssConfig = {
    small: bodySmallCss,
    medium: bodyMediumCss,
};
const helpTextCss = core_1.css `
  display: block;
  background: transparent;
  margin: 0;
`;
const helpTextMediumCss = core_1.css `
  ${helpTextCss}
  div + & {
    margin-left: ${theme_1.Spacing.large}px;
  }
`;
const helpTextSmallCss = core_1.css `
  ${helpTextCss}
  flex: 0 1;

  div + & {
    margin-top: ${theme_1.Spacing.medium}px;
  }
`;
const helpTextCssConfig = {
    small: helpTextSmallCss,
    medium: helpTextMediumCss,
};
const detailsButtonCss = core_1.css `
  ${design_system_1.Typography.bodyText800}
  color: ${theme_1.Colors.black};
  text-decoration: underline;
  font-weight: 700;
  transition: color 0.2s;
  white-space: nowrap;
  display: inline;
`;
const detailsContainerCss = core_1.css `
  overflow: hidden;
  transition: height 0.2s, opacity 0.2s;
`;
const detailsVisibleCss = (height) => core_1.css `
  height: ${height}px;
  opacity: 1;
`;
const detailsHiddenCss = core_1.css `
  height: 0;
  opacity: 0;
`;
const detailsTextCss = core_1.css `
  ${design_system_1.Typography.bodyText800}
  margin: 0 ${TEXT_INSET}px;
  padding: ${theme_1.Spacing.medium}px 0;
  border-top: 1px solid ${theme_1.Colors.grey200};
  color: #666;
  white-space: pre-line;
`;
const ErrorItem = ({ titleText, children, detailsText, titleHref, onHelpLinkClick, size, }) => {
    var _a;
    const [detailsVisible, setDetailsVisible] = react_1.useState(false);
    const detailsContainerRef = react_1.useRef(null);
    const detailsHeight = ((_a = detailsContainerRef.current) === null || _a === void 0 ? void 0 : _a.offsetHeight) || 0;
    const toggleDetailsVisible = () => setDetailsVisible(!detailsVisible);
    const titleContent = (common_utils_1.isolateComponent(react_1.Fragment, null,
        common_utils_1.isolateComponent("span", { css: iconCss, role: "presentation" },
            common_utils_1.isolateComponent(design_system_1.Icon, { color: "red600", name: "alertCircle", size: ICON_SIZE })),
        common_utils_1.isolateComponent("span", { css: titleTextCss },
            common_utils_1.isolateComponent(design_system_1.Text, { type: "subtitle700" }, titleText))));
    return (common_utils_1.isolateComponent("li", { css: containerCss },
        titleHref ? (common_utils_1.isolateComponent(design_system_1.Link, { "aria-label": `${titleText}. ${TITLE_BUTTON_LABEL}`, css: titleButtonCss, href: titleHref },
            titleContent,
            common_utils_1.isolateComponent("span", { css: iconCss, role: "presentation" },
                common_utils_1.isolateComponent(design_system_1.Icon, { color: "grey700", name: "arrowSmallRight", size: ICON_SIZE })))) : (common_utils_1.isolateComponent("div", { css: titleCss }, titleContent)),
        (children || onHelpLinkClick || detailsText) && (common_utils_1.isolateComponent(react_1.Fragment, null,
            common_utils_1.isolateComponent("div", { css: bodyCssConfig[size] },
                (children || detailsText) && (common_utils_1.isolateComponent(design_system_1.Text, { tag: "div", type: "bodyText800" },
                    children,
                    ' ',
                    detailsText && (common_utils_1.isolateComponent("button", { css: detailsButtonCss, onClick: toggleDetailsVisible }, detailsVisible ? HIDE_DETAILS_TEXT : SHOW_DETAILS_TEXT)))),
                onHelpLinkClick && (common_utils_1.isolateComponent("div", { css: helpTextCssConfig[size] },
                    common_utils_1.isolateComponent(design_system_1.Button, { ariaLabel: HELP_LINK_LABEL, onClick: onHelpLinkClick, size: "small" }, HELP_LINK_TEXT)))),
            detailsText && (common_utils_1.isolateComponent("div", { css: [
                    detailsContainerCss,
                    detailsVisible
                        ? detailsVisibleCss(detailsHeight)
                        : detailsHiddenCss,
                ] },
                common_utils_1.isolateComponent("div", { css: detailsTextCss, ref: detailsContainerRef }, detailsText)))))));
};
exports.default = ErrorItem;
